// Viewer themes. Per the Sprint 2 decision, the viewer is rendered in
// the browser at publish time (no build), so a "theme" is just a CSS
// generator parameterized by the creator's accent colour. Three for v1
// (09_VIEWER_TEMPLATE.md tentative set, trimmed): Notebook, Magazine,
// Minimal. Selecting one + republishing relights the whole site.

export interface ThemeChoice {
  id: string;
  label: string;
  vibe: string;
}

export const THEMES: ThemeChoice[] = [
  { id: "notebook", label: "Notebook", vibe: "warm serif, paper-cream — for essays + long-form" },
  { id: "magazine", label: "Magazine", vibe: "editorial, bold headings, roomy — for mixed media" },
  { id: "minimal", label: "Minimal", vibe: "system fonts, no chrome — markdown-purist" },
];

export const DEFAULT_THEME = "notebook";
export const DEFAULT_ACCENT = "#0d6e6e";

/** Shared structural CSS every theme builds on. Accent is injected. */
function baseCss(accent: string): string {
  return `:root{--accent:${accent}}
*{box-sizing:border-box}
body{margin:0;-webkit-font-smoothing:antialiased}
a{color:var(--accent);text-decoration:none}
a:hover{text-decoration:underline}
.skip{position:absolute;left:-999px}
.skip:focus{position:static}
.site-head{display:flex;align-items:baseline;gap:18px;flex-wrap:wrap}
.site-nav{display:flex;gap:16px;font-size:14px}
.site-main{margin:0 auto}
.post-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column}
.post-row{display:flex;flex-direction:column}
.post-date{font-family:ui-monospace,monospace}
.post-body img{max-width:100%;height:auto;border-radius:8px}
.post-body pre{padding:14px;border-radius:8px;overflow:auto;font-size:.85em}
.post-body code{font-family:ui-monospace,monospace;font-size:.9em}
.post-tags{display:flex;gap:8px;flex-wrap:wrap;margin:10px 0}
.tag{font-family:ui-monospace,monospace;font-size:12px;padding:2px 10px;border-radius:999px}
.searchbox{width:100%;box-sizing:border-box;padding:9px 12px;border-radius:8px;font-size:15px;margin-bottom:8px}
@media (prefers-reduced-motion: reduce){*{animation:none!important;transition:none!important}}
`;
}

function notebook(accent: string): string {
  return baseCss(accent) + `
body{background:#f5efe0;color:#1d1d1d;font-family:"Source Serif 4",Charter,Georgia,serif;font-size:18px;line-height:1.7}
.site-head{border-bottom:1px solid #e0d8c4;padding:20px 24px;max-width:720px;margin:0 auto}
.site-name{font-weight:600;font-size:20px}
.site-nav a{color:#5c574c}
.site-main{max-width:680px;padding:48px 24px}
.site-foot{max-width:680px;margin:0 auto;padding:32px 24px;color:#5c574c;font-size:14px;border-top:1px solid #e0d8c4}
.creator-name{font-size:34px;margin:0 0 8px}
.creator-bio{color:#5c574c;margin:0 0 8px}
.bio{margin-bottom:40px}
.post-list{gap:28px}
.post-link{font-size:22px;font-weight:600}
.post-date{color:#8a8474;font-size:14px}
.post-summary{margin:6px 0 0;color:#5c574c}
.post-title{font-size:34px;margin:0 0 8px;line-height:1.2}
.tag{color:var(--accent);background:rgba(13,110,110,.12)}
.post-body{margin-top:28px}
.post-body pre{background:#1d1d1d;color:#f5efe0}
.post-body blockquote{border-left:3px solid var(--accent);margin:0;padding-left:16px;color:#5c574c}
.searchbox{border:1px solid #cfc6ae;background:#fff}
`;
}

function magazine(accent: string): string {
  return baseCss(accent) + `
body{background:#fff;color:#16181d;font-family:Georgia,"Times New Roman",serif;font-size:19px;line-height:1.65}
.site-head{border-bottom:3px solid #16181d;padding:24px;max-width:900px;margin:0 auto}
.site-name{font-family:"Helvetica Neue",Arial,sans-serif;font-weight:800;font-size:26px;letter-spacing:-.02em;text-transform:uppercase}
.site-nav a{color:#16181d;font-family:"Helvetica Neue",Arial,sans-serif;text-transform:uppercase;font-size:12px;letter-spacing:.08em}
.site-main{max-width:860px;padding:48px 24px}
.site-foot{max-width:860px;margin:0 auto;padding:40px 24px;color:#666;font-size:13px;border-top:1px solid #e3e3e3;font-family:"Helvetica Neue",Arial,sans-serif}
.creator-name{font-family:"Helvetica Neue",Arial,sans-serif;font-size:44px;font-weight:800;letter-spacing:-.03em;margin:0 0 8px}
.creator-bio{color:#555;font-size:20px;margin:0 0 8px}
.bio{margin-bottom:48px;border-bottom:1px solid #e3e3e3;padding-bottom:24px}
.post-list{gap:0}
.post-row{padding:28px 0;border-bottom:1px solid #e3e3e3}
.post-link{font-family:"Helvetica Neue",Arial,sans-serif;font-size:30px;font-weight:800;letter-spacing:-.02em;color:#16181d}
.post-date{color:#999;font-size:12px;text-transform:uppercase;letter-spacing:.06em}
.post-summary{margin:8px 0 0;color:#555;font-size:18px}
.post-title{font-family:"Helvetica Neue",Arial,sans-serif;font-size:46px;font-weight:800;letter-spacing:-.03em;line-height:1.05;margin:0 0 12px}
.tag{color:#fff;background:var(--accent);font-family:"Helvetica Neue",Arial,sans-serif}
.post-body{margin-top:32px}
.post-body pre{background:#16181d;color:#fff}
.post-body blockquote{border-left:4px solid var(--accent);margin:0;padding-left:18px;color:#555;font-style:italic}
.searchbox{border:2px solid #16181d;font-family:"Helvetica Neue",Arial,sans-serif}
`;
}

function minimal(accent: string): string {
  return baseCss(accent) + `
body{background:#fff;color:#222;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;font-size:16px;line-height:1.6}
.site-head{padding:24px 20px;max-width:640px;margin:0 auto}
.site-name{font-weight:600;font-size:17px}
.site-nav a{color:#666;font-size:13px}
.site-main{max-width:640px;padding:32px 20px}
.site-foot{max-width:640px;margin:0 auto;padding:24px 20px;color:#999;font-size:13px;border-top:1px solid #eee}
.creator-name{font-size:24px;margin:0 0 6px}
.creator-bio{color:#666;margin:0 0 6px}
.bio{margin-bottom:32px}
.post-list{gap:18px}
.post-link{font-size:17px;font-weight:600;color:#222}
.post-date{color:#aaa;font-size:13px}
.post-summary{margin:4px 0 0;color:#666;font-size:15px}
.post-title{font-size:26px;margin:0 0 6px;line-height:1.25}
.tag{color:var(--accent);background:#f3f3f3}
.post-body{margin-top:24px}
.post-body pre{background:#f4f4f4;color:#222}
.post-body blockquote{border-left:3px solid #ddd;margin:0;padding-left:14px;color:#666}
.searchbox{border:1px solid #ddd}
`;
}

export function themeCss(theme: string, accent: string): string {
  switch (theme) {
    case "magazine": return magazine(accent);
    case "minimal": return minimal(accent);
    default: return notebook(accent);
  }
}
